import type {
  CoreExactMutation,
  CoreRead,
  PlatformRefusal,
  PlatformMessagingNotice,
  PlatformMessagingNoticeInbox,
  PlatformMessagingNoticeReceipt,
} from '@exylon/generated-contract-kernel/types';

/** The session inbox's four operations: the screen client `inbox` admits exactly these. */
export const NOTICE_INBOX_OPERATION_NAMES = Object.freeze({
  readInbox: 'platformMessaging.panelNoticesInbox.v1',
  seen: 'platformMessaging.panelNoticeSeen.v1',
  read: 'platformMessaging.panelNoticeRead.v1',
  dismiss: 'platformMessaging.panelNoticeDismiss.v1',
} as const);

/** Supplied by the authenticated host; neither URL parameters nor preview roles establish scope. */
export interface NoticeInboxScope {
  readonly tenantId: string;
  readonly restaurantId: string;
  readonly subjectUserId: string;
}

type NoticeInboxReadCommand = Extract<CoreRead, { operation: typeof NOTICE_INBOX_OPERATION_NAMES.readInbox }>;
type HomeBoardSeenCommand = Extract<CoreExactMutation, { operation: typeof NOTICE_INBOX_OPERATION_NAMES.seen }>;
type HomeBoardReadCommand = Extract<CoreExactMutation, { operation: typeof NOTICE_INBOX_OPERATION_NAMES.read }>;
type HomeBoardDismissCommand = Extract<CoreExactMutation, { operation: typeof NOTICE_INBOX_OPERATION_NAMES.dismiss }>;
/** One receipt on the recipient's own copy. */
export type NoticeReceiptAction = 'seen' | 'read' | 'dismiss';

type GeneratedCommands = {
  readInbox: NoticeInboxReadCommand;
  seen: HomeBoardSeenCommand;
  read: HomeBoardReadCommand;
  dismiss: HomeBoardDismissCommand;
};
type MissingGeneratedCommand = { [Name in keyof GeneratedCommands]: [GeneratedCommands[Name]] extends [never] ? Name : never }[keyof GeneratedCommands];
type AssertNoMissingCommand<Missing extends never> = Missing;
type HomeBoardGeneratedCommandProof = AssertNoMissingCommand<MissingGeneratedCommand>;

export interface NoticeInboxRefusal {
  readonly kind: 'canonical_refusal';
  readonly reason: 'authorization_denied' | 'conflict' | 'invalid_request' | 'not_found' | 'unavailable' | 'unknown';
  readonly error?: PlatformRefusal;
  readonly retry?: 'after_refresh' | 'after_permission_change' | 'manual_only';
}

/** A first page's conditional read: the revision on screen still stands, or the page with its revision. */
/** @internal */
export type NoticeInboxPageResult =
  | Readonly<{ kind: 'not_modified' }>
  | Readonly<{ kind: 'success'; canonical: PlatformMessagingNoticeInbox; entityTag?: string | null }>
  | NoticeInboxRefusal;

/** @internal */
export type NoticeInboxResult<Canonical> =
  | Readonly<{ kind: 'success'; canonical: Canonical }>
  | NoticeInboxRefusal;

/** The transport validates and unwraps; the session's authority binds the client. */
export interface NoticeInboxPort {
  readInbox(command: NoticeInboxReadCommand, entityTag?: string | null): Promise<NoticeInboxPageResult>;
  seen(command: HomeBoardSeenCommand): Promise<NoticeInboxResult<PlatformMessagingNoticeReceipt>>;
  read(command: HomeBoardReadCommand): Promise<NoticeInboxResult<PlatformMessagingNoticeReceipt>>;
  dismiss(command: HomeBoardDismissCommand): Promise<NoticeInboxResult<PlatformMessagingNoticeReceipt>>;
}

export interface NoticeInboxPage {
  readonly items: readonly PlatformMessagingNotice[];
  /** False means older notices exist beyond the returned pages. */
  readonly complete: boolean;
  /** The Backend's exact count of the whole inbox's notices without a read or dismissal receipt. */
  readonly unreadCount: number;
  /** The first page's revision, the validator of the next conditional read; null when the answer named none. */
  readonly revision?: string | null;
  /** A staff screen's corner, when the read asked for it in the same request. */
  readonly staffNotices?: readonly PlatformMessagingNotice[] | null;
}

export type NoticeInboxReadResult<Value> =
  | Readonly<{ kind: 'ready'; value: Value }>
  | Readonly<{
      kind: 'unavailable';
      reason: 'transport' | 'authorization_denied' | 'invalid_response';
      code?: string;
      message?: string;
    }>;
